import React, { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import Card from '@/components/ui/Card';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import Icon, { ValidIcon } from '@/components/ui/Icon';
import { ModelResource, ProgramResourceIcon } from './ProgramResources.utils';
import './ProgramResource.scss';

const ICON_BACKGROUND_MAP = {
  Assessments: '#005595',
  CalendarWithStar: '#475D76',
  Civics: '#005C42',
  CloudWithQuestionMark: '#0076D6',
  People: '#008800',
  PieChart: '#8C4B6A',
  PuzzlePiece: '#522E91',
  Reading: '#247F99',
  SafetyGoggles: '#FFC50C',
  StickPerson: '#C9006B',
  Stopwatch: '#5C7A66',
  TCI: '#D67419',
  Toolbox: '#800000',
};

const ICON_FILL_MAP = {
  SafetyGoggles: '#3C3C3C',
  TCI: 'transparent',
};

interface ProgramResourceCardProps {
  defaultIsOpen?: boolean;
  iconMapping: Record<number, ProgramResourceIcon>;
  resource: ModelResource;
}

export function ProgramResourceCard({ defaultIsOpen = true, iconMapping, resource }: ProgramResourceCardProps) {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const icon = iconMapping[resource.model_resource_template_id] as ValidIcon;
  const backgroundColor = ICON_BACKGROUND_MAP[icon];
  const fill = ICON_FILL_MAP[icon] || 'white';

  return (
    <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
      <Card
        className="allow-list-style tw-min-h-min tw-min-w-min tw-shadow-none tw-border tw-border-light-grey tw-rounded
         tw-border-solid"
      >
        <div className="tw-flex tw-justify-between tw-w-full tw-p-4 tw-box-border">
          <div className="tw-flex tw-items-center tw-gap-5">
            {icon && (
              <div
                className="tw-flex tw-items-center tw-justify-center tw-rounded-full tw-h-[3.5rem] tw-w-[3.5rem]"
                style={{ backgroundColor }}
              >
                <Icon fill={fill} icon={icon} size={24} />
              </div>
            )}
            <Text className="!tw-text-tci-purple" tag="h2" variant="heading4">{resource.title}</Text>
          </div>
          <Collapsible.Trigger
            className={cn(
              'btn tw-cursor-pointer tw-bg-transparent tw-p-0 tw-shadow-none tw-box-border tw-aspect-square',
              'tw-w-7 tw-h-7 tw-inline-flex tw-items-center tw-justify-center',
            )}
          >
            <Icon
              icon={isOpen ? 'ChevronDown' : 'ChevronUp'}
              size={24}
            />
          </Collapsible.Trigger>
        </div>
        <Collapsible.CollapsibleContent className="CollapsibleContent">
          <Card.Body className="tw-p-4">
            <div dangerouslySetInnerHTML={{ __html: resource.html }} />
          </Card.Body>
        </Collapsible.CollapsibleContent>
      </Card>
    </Collapsible.Root>
  );
}
